import { ColumnDef } from '@tanstack/react-table';
import { FormState } from 'react-hook-form';
import { Avatar } from '../../../../design-system/avatar/avatar/avatar';
import { ColumnHeaderWithSorting } from '../../../../design-system/data-table/column-header-with-sorting';
import { Input } from '../../../../design-system/input/input';
import { EmployeeView } from '../../../../types/entity-types';
import { cn } from '../../../../utils/cn';
import { getCurrencySymbol } from '../../../../utils/get-currency-symbol';
import { PayrollFormData } from './financial-information-form';

export type PayrollEmployee = EmployeeView & {
  netPay: string;
  reference: string;
};

export const financialInformationTableColumnDefinition: ColumnDef<PayrollEmployee>[] = [
  {
    id: 'avatar',
    header: () => null,
    cell: ({ row }) => {
      const employee = row.original;
      const fullName = `${employee.firstName} ${employee.lastName}`;
      return <Avatar name={fullName} size="sm" />;
    },
    enableSorting: false,
    enableHiding: false,
    size: 6,
    minSize: 6,
  },
  {
    accessorKey: 'firstName',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="First Name" />,
    cell: ({ row }) => <span className="text-sm">{row.original.firstName}</span>,
    size: 14,
    minSize: 14,
  },
  {
    accessorKey: 'lastName',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Last Name" />,
    cell: ({ row }) => <span className="text-sm">{row.original.lastName}</span>,
    size: 18,
    minSize: 18,
  },
  {
    id: 'bankAccount',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Bank Account" />,
    cell: ({ row, table }) => {
      const bankAccount = table.options.meta?.showAccountOptions
        ? (() => {
            const sortCode = row.original.destination?.sortCode;
            const formattedSortCode = sortCode?.replace(/(\d{2})(\d{2})(\d{2})/, '$1-$2-$3');
            const accountNumber = row.original.destination?.accountNumber;
            const displayValue = [formattedSortCode, accountNumber].join(' ');
            return displayValue;
          })()
        : row.original.destination?.iban;
      return <span className="text-sm">{bankAccount}</span>;
    },
    size: 25,
  },
  {
    id: 'reference',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Payment Reference" />,
    cell: ({ row }) => {
      return <span className="text-sm">{row.original.reference}</span>;
    },
    accessorKey: 'reference',
    size: 16,
    minSize: 16,
  },
  {
    id: 'netPay',
    header: ({ column }) => (
      <div className="flex justify-end">
        <ColumnHeaderWithSorting column={column} label="Net Pay" />
      </div>
    ),
    cell: ({ row, table }) => {
      const employee = row.original;
      const currency = employee.destination?.currency;
      const currencySymbol = getCurrencySymbol(currency);
      const onNetPayChange = table.options.meta?.onNetPayChange;
      const formState = table.options.meta?.formState as FormState<PayrollFormData>;
      const hasErrorMessage = Boolean(formState?.errors?.employees?.[row.index]?.netPay?.message);

      return (
        <Input
          type="text"
          value={employee.netPay ? `${currencySymbol}${employee.netPay}` : ''}
          onChange={(e) => {
            const value = e.target.value;
            // Remove currency symbol and non-numeric chars, then validate
            const stringValue = value.replace(currencySymbol, '').replace(/[^\d.]/g, '');

            // Allow empty string or valid decimal numbers with up to 2 decimal places
            if (stringValue === '' || /^\d*\.?\d{0,2}$/.test(stringValue)) {
              if (onNetPayChange) {
                onNetPayChange(employee.id, stringValue);
              }
            }
          }}
          onFocus={(e) => {
            if (e.target.value === currencySymbol + '0') {
              e.target.value = currencySymbol;
            }
          }}
          placeholder={`${currencySymbol}0.00`}
          className={cn(
            'w-full rounded-lg border-border bg-white text-right text-sm',
            '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
            { 'border-destructive': formState?.isSubmitted && hasErrorMessage },
          )}
          // Prevent row click when clicking on input
          onClick={(e) => e.stopPropagation()}
        />
      );
    },
    accessorKey: 'netPay',
    size: 13,
    minSize: 13,
  },
];
